/** @typedef {{ id: string, displayName: string, value: string, el: node, selected: boolean, rank: number }} MultiSelectItem */

const GLOBAL_PROPERTY_PREFIX = '__multiSelect__';

const SELECTOR = 'vjs-multi-select';

const DEFAULT_DISPLAY_ITEM_CLASS = 'c-multi-select-item';
const DEFAULT_CHECKBOX_SELECTOR = '.js-multiselect-checkbox';
const DEFAULT_DISPLAY_DELIMITER = ', ';
const DEFAULT_INPUT_DELIMITER = '|';

class MultiSelect {
    constructor(el) {
        this.el = el;
        this.inputEl = this.el.querySelector(this.el.dataset.target);
        this.displayEl = this.el.querySelector(this.el.dataset.displayTarget);
        this.clearEl = this.el.querySelector(this.el.dataset.clearButton);
        this.checkboxEls = this.el.dataset.checkboxSelector ? this.el.querySelectorAll(this.el.dataset.checkboxSelector) : this.el.querySelectorAll(DEFAULT_CHECKBOX_SELECTOR);

        /** @type {Array<MultiSelectItem>} */
        this.checkboxArr = [];

        this.rank = Number(this.el.dataset.minRank) || 1;
        this.enforceRank = !!this.el.dataset.rank;
        this.emptyText = this.el.dataset.emptyText || '';
        this.max = Number(this.el.dataset.max) || null;
        this.selectionDisabled = false;

        this.globalProp = '';

        this.valueClass = this.el.dataset.displayItemClass || DEFAULT_DISPLAY_ITEM_CLASS;
        this.displayDelimiter = DEFAULT_DISPLAY_DELIMITER;
        this.inputDelimiter = DEFAULT_INPUT_DELIMITER;

        if(this.inputEl && this.checkboxEls.length > 0) {
            this.init();
        }
    }

    init() {
        this.globalProp = `${GLOBAL_PROPERTY_PREFIX}${this.inputEl.id}_`;

        this.setupCheckboxArr();
        this.setupBindedListeners();
        this.setupListeners();

        if(this.enforceRank) {
            this.displayRanks();
        }

        if(this.max) {
            this.disableAtMax(this.max <= (this.rank - 1));
        }

        this.display();
        this.updateGlobal();
    }

    destroy() {
        this.destroyListeners();
        delete window[this.globalProp];
    }

    setupCheckboxArr() {
        this.checkboxArr = [ ...this.checkboxEls ].map(el => {
            let checkbox = {};
            checkbox.el = el;
            checkbox.id = el.id;
            checkbox.displayName = el.dataset.displayName || el.value;
            checkbox.value = el.value;
            checkbox.selected = el.checked;
            checkbox.rank = checkbox.selected ? parseInt(el.dataset.rank) || null : null;

            if(checkbox.selected) {
                this.rank++;
            }

            return checkbox;
        });
    }

    setupBindedListeners() {
        this.checkboxArr.forEach((el, i) => {
            this['bindedToggle' + i] = this.toggle.bind(this, i);
        });
    }

    setupListeners() {
        this.clearEl?.addEventListener('click', this.bindedClear);

        this.checkboxArr.forEach((checkbox, i) => {
            checkbox.el.addEventListener('change', this['bindedToggle' + i]);
        });
    }

    destroyListeners() {
        this.clearEl?.removeEventListener('click', this.bindedClear);

        this.checkboxArr.forEach((checkbox, i) => {
            checkbox.el.removeEventListener('change', this['bindedToggle' + i]);
        });
    }

    toggle(i) {
        this.checkboxArr[i].selected = !this.checkboxArr[i].selected;

        // if checkbox deselected, decrease all higher rankings
        if(!this.checkboxArr[i].selected) {
            this.updateRanks(this.checkboxArr[i].rank);
        }

        this.checkboxArr[i].rank = this.checkboxArr[i].selected ? this.rank : null;
        this.rank = this.checkboxArr[i].selected ? ++this.rank : --this.rank;

        this.display();

        if(this.enforceRank) {
            this.disablePrevRanks();
            this.displayRanks();
        }

        if(this.max) {
            this.disableAtMax(this.max <= (this.rank - 1));
        }

        this.updateGlobal();
    }

    clear() {
        this.checkboxArr.forEach(checkbox => {
            checkbox.el.checked = false;
            checkbox.rank = null;
            checkbox.selected = false;
        });

        this.rank = 1;

        this.display();

        if(this.enforceRank) {
            this.disablePrevRanks();
            this.displayRanks();
        }

        if(this.max) {
            this.disableAtMax();
        }

        this.updateGlobal();
    }

    updateRanks(rank) {
        this.checkboxArr = this.checkboxArr.map(checkbox => {
            if(checkbox.rank > rank) {
                checkbox.rank = --checkbox.rank;
            }
            return checkbox;
        });
    }

    updateGlobal() {
        window[this.globalProp] = [ ...this.checkboxArr ];
    }

    disableAtMax(isMax) {
        if(this.selectionDisabled && !isMax) {
            this.checkboxArr.forEach(checkbox => checkbox.el.removeAttribute('disabled'));

            if(this.enforceRank) {
                this.disablePrevRanks();
            }
        } else if(isMax) {
            this.checkboxArr.forEach(checkbox => {
                if(!checkbox.selected) {
                    checkbox.el.setAttribute('disabled', 'disabled');
                }
            });
        }

        this.selectionDisabled = isMax;
    }

    disablePrevRanks() {
        this.checkboxArr.forEach(checkbox => {
            checkbox.el.removeAttribute('disabled');

            if(checkbox.selected && checkbox.rank < (this.rank - 1)) {
                checkbox.el.setAttribute('disabled', 'disabled');
            }
        });
    }

    displayRanks() {
        this.checkboxArr.forEach(checkbox => {
            const elements = this.el.querySelectorAll(`[data-display-rank="${checkbox.id}"]`);
            elements.forEach(el => {
                if(checkbox.rank) {
                    el.textContent = checkbox.rank;
                } else {
                    el.textContent = '';
                }
            });
        });
    }

    display() {
        const selectedArr = this.checkboxArr
            .filter(checkbox => checkbox.selected) // filter to only selected checkboxes
            .sort((a, b) => a.rank - b.rank) // order ascending by rank
            .map(({ displayName, value, rank }) => { // return only required props
                return {
                    displayName,
                    value,
                    rank
                };
            });

        // update input and display values
        this.inputEl.value = selectedArr
            .map(({ value }) => value) // return array of values
            .join(this.inputDelimiter); // merge into string delimeted by specified character

        if(this.displayEl) {
            const content = selectedArr
                .map(({ displayName }) => `<span class="${this.valueClass}">${displayName}`) // return array of display names
                .join(`${this.displayDelimiter}&nbsp;</span>`);

            if(content) {
                this.displayEl.innerHTML = `${content}</span>`;
            } else {
                this.displayEl.innerHTML = this.emptyText;
            }
        }
    }

    // binded event handlers
    bindedClear = this.clear.bind(this);
}

let instanceArr = [];

export function destroy() {
    instanceArr.forEach(instance => instance?.destroy());
    instanceArr.splice(0, instanceArr.length);
}

export default function() {
    const elements = document.querySelectorAll(`[${SELECTOR}]`);
    elements?.forEach((element, i) => {
        instanceArr[i] = new MultiSelect(element);
    });
}
